import React from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router';
import { captureException } from '@sentry/react';
import { httpsCallable } from '@firebase/functions';

import type { CustomStep, StatusActivityStatus } from '@shared/patient-intake-status';

import { functions } from '../../../../../firebase-config.js';
import { useAuth, useChatRoleSelection } from '../../../../../hooks/index.js';
import { usePatientModalData } from '../../../../../components/patient-management/PatientPage/usePatientModalData.ts';
import { usePatientIntakeStatusContext } from '../../hooks';
import type { BaseActionPromptProps } from './CustomSteps.types.ts';
import { CustomStepActionPrompt } from './CustomStepActionPrompt.tsx';

const updateIntakeStatusFunction = httpsCallable<{ patientId: string; stepId: string }>(
  functions,
  'updatePatientMessageIntakeStatus_v2',
);

type UpdateMessageStatusParams = {
  patientId: string;
  stepId: string;
  currentStepStatus: StatusActivityStatus;
};

/**
 * FUTURE: @keeganpotgieter we are pre-emptively updating the status to PENDING. This is not ideal.
 */
const updateMessageStatus = ({ patientId, stepId, currentStepStatus }: UpdateMessageStatusParams) => {
  if (currentStepStatus === 'PENDING') {
    return;
  }

  updateIntakeStatusFunction({ patientId, stepId }).catch((error) => {
    if (import.meta.env.DEV) {
      console.error('Failed to update message intake status:', error);
    }

    captureException(error, {
      tags: { issueIn: 'MessageAction.updateMessageStatus' },
      extra: {
        stepId,
        patientId,
      },
    });
  });
};

interface MessageActionProps extends BaseActionPromptProps {
  step: CustomStep['MESSAGE'];
  activeHealthCategoryId: string;
}

export const MessageAction: React.FC<MessageActionProps> = ({
  step,
  activeHealthCategoryId,
  onEdit,
  onDelete,
  onToggleDisabled,
}) => {
  const navigate = useNavigate();
  const { user, userType } = useAuth();
  const { customSteps, isHealthcareProvider, patientId } = usePatientIntakeStatusContext();
  const { setSelectedChatRole } = useChatRoleSelection();
  const { setModalData } = usePatientModalData();

  const handleOnClick = async () => {
    const { messageContext } = step.metadata;

    if (!user || !userType) {
      toast.error('Please login to continue');

      return;
    }

    if (!isHealthcareProvider) {
      updateMessageStatus({
        patientId,
        stepId: step.id,
        currentStepStatus: step.currentStatus?.status ?? 'NOT_STARTED',
      });

      navigate(`/messages/${messageContext}`, {
        state: { activeHealthCategoryId, stepId: step.id, step },
      });

      return;
    }

    if (userType === 'nurse' || userType === 'director' || messageContext === userType) {
      setModalData({
        modalName: 'SendMessage',
      });

      setSelectedChatRole(messageContext);

      return;
    }

    toast(`You do not have access to the ${messageContext} chat`, {
      description: 'The nursing team will be able to review any messages and update the status',
    });
  };

  if (!customSteps) {
    return null;
  }

  return (
    <CustomStepActionPrompt
      step={step}
      description={step.description}
      onClick={handleOnClick}
      onEdit={onEdit}
      onDelete={onDelete}
      onToggleDisabled={onToggleDisabled}
    />
  );
};
